import { Component } from '@angular/core';
import { Chart, ChartConfiguration, ChartDataset } from 'chart.js';
import { SalesInsightsSeriesResponse } from '../../models/sales-insights.model';
import { salesInsightsRgba, salesInsightsThemeColors } from '../../utils/sales-insights-theme';
import { SalesInsightsSeriesBaseComponent } from '../sales-insights-base/sales-insights-series-base.component';

@Component({
  selector: 'lib-sales-insights-line-chart',
  standalone: false,
  templateUrl: './sales-insights-line-chart.component.html',
  styleUrl: './sales-insights-line-chart.component.scss'
})
export class SalesInsightsLineChartComponent extends SalesInsightsSeriesBaseComponent {
  datasets: ChartDataset<'line'>[] = [];
  chartOptions: ChartConfiguration<'line'>['options'];

  protected onData(data: SalesInsightsSeriesResponse | null): void {
    const entries = this.visibleEntries(data);
    const theme = salesInsightsThemeColors();
    const line = { tension: 0.3, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 8 };

    this.labels = entries.map(entry => this.formatKey(entry.key));
    this.datasets = [];
    if (this.hasPrevious(entries)) {
      this.datasets.push({
        ...line,
        label: this.seriesLabel(entries[0]?.previousKey, 'SALES_INSIGHTS_PREVIOUS_PERIOD'),
        data: entries.map(entry => entry.previous?.[this.measure] ?? 0),
        borderColor: salesInsightsRgba(this.colorRgb, 0.35),
        backgroundColor: salesInsightsRgba(this.colorRgb, 0.35),
        pointHoverBackgroundColor: salesInsightsRgba(this.colorRgb, 0.5)
      });
    }
    this.datasets.push({
      ...line,
      label: this.seriesLabel(entries[0]?.key, 'SALES_INSIGHTS_CURRENT_PERIOD'),
      data: entries.map(entry => entry.current?.[this.measure] ?? 0),
      borderColor: salesInsightsRgba(this.colorRgb, 0.95),
      backgroundColor: salesInsightsRgba(this.colorRgb, 0.08),
      pointHoverBackgroundColor: salesInsightsRgba(this.colorRgb, 1),
      fill: 'origin'
    });
    this.chartOptions = this.withSolidLegendSwatches(this.baseOptions<'line'>(theme));
  }

  // A line dataset's legend box is filled with its area colour, which is almost transparent here.
  private withSolidLegendSwatches(
    options: ChartConfiguration<'line'>['options']
  ): ChartConfiguration<'line'>['options'] {
    const labels = options?.plugins?.legend?.labels;
    if (labels) {
      labels.generateLabels = chart =>
        Chart.defaults.plugins.legend.labels
          .generateLabels(chart)
          .map(item => ({ ...item, fillStyle: item.strokeStyle }));
    }
    return options;
  }
}
